import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiVerifyEmail, apiErrorCode } from '../api';
import { useAuth } from '../context/AuthContext';
import BandzLogo from '../components/BandzLogo';
import { pageLoadErrorText } from '../components/PageLoadError';

/**
 * Public e-mail verification page (/verify-email?token=…).
 *
 * Public route, OUTSIDE RequireAuth: the visitor arrives from the e-mail link
 * without a session. The token is single use and valid 48 h. On success the
 * server creates the session, so we only have to push the user into the app
 * (and refresh RequireAuth's user state) — no password is ever typed here.
 *
 * A missing / used / expired / unknown token shows a localized message and a
 * link to /login (400 `invalid_token`).
 */
export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get('token') || '';
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending');
  const [error, setError] = useState<string | null>(null);
  // The token must be consumed exactly once, even if React re-runs the effect
  // (StrictMode / a re-render) — a second POST would answer invalid_token.
  const started = useRef(false);
  // Lot 3a — the retry has to be able to REPLAY the call, so the once-only
  // guard is re-armed by a token bump instead of blocking the second attempt.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setState('error');
      setError(t('verify.missingToken'));
      return;
    }

    let cancelled = false;
    setState('pending');
    setError(null);
    apiVerifyEmail(token)
      .then((res) => {
        if (cancelled) return;
        if (res?.user) {
          setUser(res.user);
          // Let ProfileProvider load the bands now that the session exists.
          window.dispatchEvent(new Event('bandz-auth-login'));
        }
        setState('ok');
        window.setTimeout(() => { if (!cancelled) navigate('/', { replace: true }); }, 1500);
      })
      .catch((err) => {
        if (cancelled) return;
        setState('error');
        const code = apiErrorCode(err);
        // A REJECTED TOKEN is not a load failure: the server answered.
        // Anything else (network down, 5xx, no answer) is — and it used to be
        // reported as "invalid link", sending the visitor off to reset a link
        // that was perfectly fine. The two now lead to different states.
        if (code === 'invalid_token') {
          setError(t('verify.invalidToken'));
        } else {
          setError(pageLoadErrorText(t));
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, attempt]);

  return (
    <div className="login-page">
      <div className="login-wrap">
        <BandzLogo className="login-logo" />
        <div className="login-card">
          {state === 'pending' && (
            <>
              <h1>{t('verify.title')}</h1>
              <p className="login-subtitle">{t('verify.pending')}</p>
            </>
          )}
          {state === 'ok' && (
            <>
              <h1>{t('verify.successTitle')}</h1>
              <p className="login-subtitle">{t('verify.success')}</p>
            </>
          )}
          {state === 'error' && (
            <>
              <h1>{t('verify.errorTitle')}</h1>
              <p className="login-subtitle">{error}</p>
              {/* Lot 3a — Retry only where a retry can actually help: the token
                  was REJECTED (`invalidToken`) means the link is dead, so the
                  useful actions stay the two links below; anything else is a
                  failed call, and the visitor gets a retry that replays it. */}
              {error && error !== t('verify.invalidToken') && error !== t('verify.missingToken') && (
                <button
                  type="button"
                  className="btn btn-primary"
                  data-page-load-error-retry
                  onClick={() => {
                    started.current = false;
                    setAttempt((n) => n + 1);
                  }}
                >
                  {t('pageLoadError.retry')}
                </button>
              )}
              <p className="signup-alt">
                <Link to="/login">{t('verify.goToSignIn')}</Link>
              </p>
              <p className="signup-alt">
                <Link to="/signup">{t('verify.goToSignUp')}</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}